/**
 * API Endpoints Constants
 * Centralized location for all API endpoint URLs used in the application
 */

// Base API path
export const API_BASE = '/api';

// Authentication endpoints
export const AUTH_ENDPOINTS = {
  SIGNIN: `${API_BASE}/auth/signin`,
  SIGNUP: `${API_BASE}/auth/signup`,
  SIGNOUT: `${API_BASE}/auth/signout`,
  GOOGLE: `${API_BASE}/auth/google`,
  RESET_PASSWORD: `${API_BASE}/auth/reset-password`,
  UPDATE_PASSWORD: `${API_BASE}/auth/update-password`,
  CLAIM_ANON_USER: `${API_BASE}/auth/users/anon/claim`,
  FINALIZE_ANON: `${API_BASE}/auth/users/anon/finalize`,
  RESET_SESSION: `${API_BASE}/auth/reset-session`,
} as const;

// Protected endpoints
export const PROTECTED_ENDPOINTS = {
  PROFILE: `${API_BASE}/protected/profile`,
  SUGGESTIONS: `${API_BASE}/protected/suggestions`,
  TEMPLATES: `${API_BASE}/protected/templates`,
  FIELDS: `${API_BASE}/protected/fields`,
  TEMPLATE_CONFIGS: `${API_BASE}/protected/template_configs`,
  CLIENT: `${API_BASE}/protected/client`,
  // Flow participant endpoints
  FLOW_PARTICIPANTS: (flowId: string) =>
    `${API_BASE}/flows/${flowId}/participants`,
  FLOW_PARTICIPANT_BY_ID: (flowId: string, participantId: string) =>
    `${API_BASE}/flows/${flowId}/participants/${participantId}`,
  FLOW_PARTICIPANTS_ROUTING_ORDER: (flowId: string) =>
    `${API_BASE}/flows/${flowId}/participants/routing-order`,
  LISTING: `${API_BASE}/listing`,
} as const;

// External API endpoints
export const EXTERNAL_API_ENDPOINTS = {
  PLACES_AUTOCOMPLETE: `${API_BASE}/places/autocomplete`,
  PLACES_DETAILS: `${API_BASE}/places/details`,
} as const;

// Auth callback and redirect URLs
export const AUTH_ROUTES = {
  CALLBACK_CLERK: '/auth/callback/clerk',
  CALLBACK_SUPABASE: '/auth/callback/supabase',
  LOGIN: '/login',
  LOGIN_EMAIL_LINK: '/login/email-link',
  SIGNUP: '/signup',
  VERIFY_EMAIL: '/verify-email',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  AUTH_CODE_ERROR: '/auth/auth-code-error',
} as const;

// External links (legal/marketing pages on main website)
export const EXTERNAL_LINKS = {
  PRIVACY: 'https://goclai.com/privacy',
  TERMS: 'https://goclai.com/tos',
  CONTACT_EMAIL: 'hi@goclai.com',
  CONTACT: 'mailto:hi@goclai.com',
} as const;

// Main application routes
export const APP_ROUTES = {
  LANDING: '/',
  HOME: '/home',
  JOIN: '/join',
  DEALS: '/deals',
  MY_DEALS: '/my-deals',
  MY_PAYMENTS: '/my-payments',
  DEAL_DETAIL: (dealId: string) => `/deals/${dealId}`,
  PACKAGE_DETAIL: (dealId: string, packageId: string) =>
    `/deals/${dealId}/packages/${packageId}`,
  PACKAGE_SIGNING: (dealId: string, packageId: string, submissionId: string) =>
    `/deals/${dealId}/packages/${packageId}/docs/signing?submissionId=${submissionId}`,
  PACKAGE_PAYMENTS: (dealId: string, packageId: string) =>
    `/deals/${dealId}/packages/${packageId}/payments`,
  TEMPLATES: '/templates',
  SETTINGS: '/settings',
  ONBOARDING: '/onboarding',
} as const;

// All API endpoints combined for easy access
export const API_ENDPOINTS = {
  ...AUTH_ENDPOINTS,
  ...PROTECTED_ENDPOINTS,
  ...EXTERNAL_API_ENDPOINTS,
} as const;

// All routes combined for easy access
export const ALL_ROUTES = {
  ...AUTH_ROUTES,
  ...APP_ROUTES,
} as const;

// TimeLimit for tanstack query
// TODO: this is not part of the API, it's a global constant for the application
export const STALE_TIME = 1000 * 60 * 5; // 5 minutes
export const GC_TIME = 1000 * 60 * 10; // 10 minutes

// HTTP Methods supported by each endpoint
export const API_METHODS = {
  // Auth endpoints
  [AUTH_ENDPOINTS.SIGNIN]: ['POST'],
  [AUTH_ENDPOINTS.SIGNUP]: ['POST'],
  [AUTH_ENDPOINTS.SIGNOUT]: ['POST'],
  [AUTH_ENDPOINTS.GOOGLE]: ['POST'],
  [AUTH_ENDPOINTS.RESET_PASSWORD]: ['POST'],
  [AUTH_ENDPOINTS.UPDATE_PASSWORD]: ['POST'],
  [AUTH_ENDPOINTS.RESET_SESSION]: ['POST'],

  // Protected endpoints
  [PROTECTED_ENDPOINTS.PROFILE]: ['GET', 'PUT'],
  [PROTECTED_ENDPOINTS.SUGGESTIONS]: ['GET'],
  [PROTECTED_ENDPOINTS.TEMPLATES]: ['GET', 'POST', 'PUT', 'DELETE'],
  [PROTECTED_ENDPOINTS.LISTING]: ['GET', 'POST'],
  // External API endpoints
  [EXTERNAL_API_ENDPOINTS.PLACES_AUTOCOMPLETE]: ['GET'],
  [EXTERNAL_API_ENDPOINTS.PLACES_DETAILS]: ['GET'],
} as const;

/**
 * URL prefixes whose pages resolve auth at runtime via tokens or
 * cookies rather than a Clerk session. Two consumers honor this
 * list:
 *
 *   - Middleware (server): gates on the credential itself via
 *     `hasDealSigningCredential`, admitting a URL token, the
 *     submission-token cookie, a deal-bound flow-token marker,
 *     or a verified scoped/anon cookie, and redirecting everyone
 *     else to /login with the return path. The page-level gate
 *     stays the authority on permission.
 *   - Error boundary (client): skips its reflexive 401 redirect
 *     so the auto-signin dance can complete.
 *
 * The `/deals/` prefix covers two route families that land on
 * different page-level authorities: /deals/[deal_id]/* under
 * DealLayout's denyOrRedirect() → notFound(), and
 * /deals/invitations/* under a bare requirePageAuth(). Both want
 * the same middleware treatment today.
 *
 * Both consumers match by prefix (pathname.startsWith / regex
 * `^${prefix}`). Keep the list narrow — file location under
 * src/app/(public)/ remains the default mechanism for truly
 * public URLs.
 */
export const AUTO_SIGNIN_PREFIXES = ['/deals/'] as const;
// Export all endpoints as a single object for convenience
export default API_ENDPOINTS;
